<template>
  <Panel
    :id="id"
    class="stop-edit elevated"
    :class="{
      pickup: isPickup,
      dropoff: isDropoff,
      warning: !isPickup && !isDropoff,
    }"
    tabindex="-1"
  >
    <template #header>
      <div class="shipment-stop-header">
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="shipment-stop-type"
          :errors="v$.stopType.$errors"
          :label="
            t('common.assignParam', {
              param: t('common.paramTypes', {
                param: t('shipments.stops'),
              }),
            })
          "
          class="shipment-stop-header-dropdown-wrapper"
        >
          <PickupIcon v-if="isPickup" />
          <DropoffIcon v-else-if="isDropoff" />

          <RygenDropdown
            v-if="allowChangingStopType"
            :id="inputId"
            v-model="internalValue.stopType"
            v-bind="ariaProps"
            class="shipment-stop-type"
            :class="[inputClasses]"
            :invalid="invalid"
            :options="stopOptions"
            :placeholder="
              t('common.assignParam', {
                param: t('common.paramTypes', {
                  param: t('shipments.stops'),
                }),
              })
            "
            option-label="label"
            option-value="value"
          />
          <span v-else-if="isPickup">
            {{
              internalValue.sequenceNumber === 1
                ? t('orders.origin')
                : t('shipments.pickups')
            }}
          </span>
          <span v-else-if="isDropoff">
            {{
              internalValue.sequenceNumber === stops.length
                ? t('orders.destination')
                : t('shipments.dropoffs')
            }}
          </span>
        </InputWrapper>

        <div class="shipment-stop-header-address">
          <div class="flex flex-gap-1 align-items-center">
            <a
              v-if="isFiconLocation && hasLocationUpdate"
              :href="
                generateLocationLink(
                  internalValue.location,
                  selectedDomain?.naturalId,
                )
              "
              class="location-link"
            >
              {{ internalValue.location.locationName }}
            </a>
            <span v-else class="location-name font-normal text-gray-darkest">
              {{
                isFiconLocation
                  ? internalValue.location?.locationName
                  : t('common.newParam', { param: t('locations.locations') })
              }}
            </span>

            <template
              v-if="
                internalValue.location.addressLine1 ||
                internalValue.location.cityStatePostalCode
              "
            >
              <span>-</span>

              <span v-if="internalValue.location.addressLine1">
                {{ internalValue.location.addressLine1 }}
              </span>

              <span v-if="internalValue.location.cityStatePostalCode">
                {{ internalValue.location.cityStatePostalCode }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template #icons>
      <RygenButton
        v-if="isFiconLocation || v$.$anyDirty"
        mode="tertiary"
        :label="t('orders.clearAndStartOver')"
        :disabled="disabled"
        :data-testid="`stop-edit-${internalValue.sequenceNumber}-clear-button`"
        @click="onClear"
      />

      <RygenButton
        v-if="removable"
        mode="tertiary"
        :label="t('common.removeParam', { param: t('shipments.stops') })"
        :disabled="disabled"
        :severity="Severity.DANGER"
        icon="pi pi-trash"
        :data-testid="`stop-edit-${internalValue.sequenceNumber}-remove-button`"
        @click="emit('remove', internalValue)"
      />

      <RygenButton
        v-if="!disabled"
        mode="tertiary"
        :label="
          isFiconLocation ? t('common.changes') : t('address.addressBook')
        "
        @click="isShowingAddressBook = true"
      />

      <ExpandButton
        v-model="isExpanded"
        :target-id="`${id}-content`"
        :data-testid="`stop-edit-${internalValue.sequenceNumber}-expand-button`"
        no-labels
      />
    </template>

    <template #default>
      <StopEditContent
        :id="`${id}-content`"
        v-model="internalValue"
        v-model:expanded="isExpanded"
        :stops="stops"
        :primary-contact="primaryContact"
        :disabled="disabled"
        :show-order-reference-numbers="showOrderReferenceNumbers"
        :show-add-to-address-book-switch="showAddToAddressBookSwitch"
        @address-book-select="onShippingPointSelect"
        @autocomplete="emit('autocomplete', $event)"
        @clear="onClear"
      />

      <AddressBookModal
        v-model:visible="isShowingAddressBook"
        v-model:search-criteria="searchCriteria"
        :header="
          t('common.addParam', {
            param: isPickup ? t('orders.origin') : t('orders.destination'),
          })
        "
        :search-results="searchResults"
        :type="isPickup ? 'pickup' : 'dropoff'"
        :data-testid="`stop-edit-address-book-modal-${internalValue.sequenceNumber}`"
        @select="onShippingPointSelect"
      />
    </template>

    <template v-if="showAddToShipmentButtons" #footer>
      <RygenButton
        mode="secondary"
        :label="t('common.cancelAndClose')"
        @click="emit('cancel')"
      />
      <RygenButton
        mode="primary"
        :label="t('multistop.addToShipment')"
        icon="pi pi-plus"
        @click="emit('add-to-shipment')"
      />
    </template>
  </Panel>
</template>

<script lang="ts" setup>
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast, useValidations } from '@rygen/rygen-ui/composables';
import {
  Address,
  DropdownOption,
  ErrorResponse,
  Loadable,
  Location,
  Optional,
  Severity,
  ShippingPointDetails,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useShippingPoints } from '@/composables';
import {
  DoesUserHavePermissionEntityType,
  EmbeddableContact,
  EmbeddableLocation,
  OrderShippingPoint,
  ShipmentStopType,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { generateLocationLink } from '@/util';

import StopEditContent from '@/components/entry/StopEditContent.vue';
import { type Stop } from '@/components/entry/StopEntrySection.vue';
import DropoffIcon from '@/components/shippingPoint/DropoffIcon.vue';
import PickupIcon from '@/components/shippingPoint/PickupIcon.vue';
import AddressBookModal from '@rygen/rygen-ui/components/address/AddressBookModal.vue';
import ExpandButton from '@rygen/rygen-ui/components/button/ExpandButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import Panel from 'primevue/panel';

const isExpanded = defineModel('expanded', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  modelValue: {
    type: Object as PropType<Stop>,
    default: () => new OrderShippingPoint(),
  },
  id: {
    type: String,
    required: true,
  },
  stops: {
    type: Array as PropType<Stop[]>,
    default: () => [],
  },
  errorResponse: {
    type: Object as PropType<ErrorResponse>,
    default: () => undefined,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  allowChangingStopType: {
    type: Boolean,
    default: () => false,
  },
  removable: {
    type: Boolean,
    default: () => false,
  },
  showOrderReferenceNumbers: {
    type: Boolean,
    default: () => false,
  },
  showAddToShipmentButtons: {
    type: Boolean,
    default: () => false,
  },
});
const {
  modelValue,
  id,
  stops,
  errorResponse,
  disabled,
  allowChangingStopType,
  removable,
  showOrderReferenceNumbers,
  showAddToShipmentButtons,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'add-to-shipment'): void;
  (event: 'autocomplete', isAutocompleteSelection: boolean): void;
  (event: 'cancel'): void;
  (event: 'remove', stop: Stop): void;
  (event: 'update:model-value', modelValue: Stop): void;
}>();

const Clazz = Object.getPrototypeOf(modelValue.value).constructor;
const internalValue: Ref<Stop> = ref(new Clazz(modelValue.value));

const { t } = useI18n();
const { selectedDomain } = useCurrentUserStore();
const { resetSearch, searchCriteria, searchResults } = useShippingPoints();
const toast = useToast();
const { setServerErrors, v$ } = useValidations(internalValue, {
  stopType: {},
});

const isShowingAddressBook = ref(false);
const doesUserHaveUpdateLocationPermission = ref(false);

const primaryContact: Ref<Loadable<EmbeddableContact>> = ref({
  loading: false,
});

const isTimezoneLoading = ref(false);
const timezoneCancelRequest: Ref<CancelTokenSource | undefined> = ref();

const stopOptions: DropdownOption<ShipmentStopType>[] = [
  {
    label: t('shipments.pickups'),
    value: ShipmentStopType.PICKUP,
  },
  {
    label: t('shipments.dropoffs'),
    value: ShipmentStopType.DROP_OFF,
  },
];

const isPickup = computed(
  () => internalValue.value.stopType === ShipmentStopType.PICKUP,
);

const isDropoff = computed(
  () => internalValue.value.stopType === ShipmentStopType.DROP_OFF,
);

const hasLocationCreate = computed(() =>
  authorization.hasAuthority('location', 'create'),
);

const hasLocationUpdate = computed(() =>
  authorization.hasAuthority('location', 'update'),
);

const isFiconLocation = computed(
  () => !!internalValue.value.location.locationId,
);

const showAddToAddressBookSwitch = computed(() => {
  return isFiconLocation.value
    ? doesUserHaveUpdateLocationPermission.value
    : hasLocationCreate.value;
});

const loadDoesUserHavePermission = async (ficonLocationId: number) => {
  try {
    const hasPermission = await api.user.doesCurrentUserHavePermission(
      DoesUserHavePermissionEntityType.LOCATION,
      ficonLocationId,
      'location:update',
      true,
    );

    // Ignore the response if the stop changed locations while it was loading
    if (internalValue.value.location.locationId === ficonLocationId) {
      doesUserHaveUpdateLocationPermission.value = hasPermission;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading user permission', e);
  }
};

const onClear = () => {
  const Clazz = Object.getPrototypeOf(internalValue.value).constructor;
  internalValue.value = new Clazz({
    id: internalValue.value.id,
    sequenceNumber: internalValue.value.sequenceNumber,
    stopType: internalValue.value.stopType,
    location: new EmbeddableLocation(),
    contact: new EmbeddableContact(),
  });
  nextTick(() => v$.value.$reset());
};

const onShippingPointSelect = (location: Location) => {
  if (!location.id) {
    return;
  }

  internalValue.value.location = EmbeddableLocation.fromLocation(location);
  internalValue.value.shippingPointDetails = new ShippingPointDetails(
    location.shippingPointDetails,
  );

  if (!location.timeZone) {
    getTimeZoneDebounced();
  }
  getPrimaryContact(location.id);
  setDefaultAccessorials(internalValue.value.shippingPointDetails);
};

const getPrimaryContact = async (locationId: number) => {
  try {
    primaryContact.value.loading = true;
    primaryContact.value.data =
      await api.shippingPoint.findPrimaryContactForShippingPoint(locationId);
    internalValue.value.contact = primaryContact.value.data;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // No primary contact is fine
      internalValue.value.contact = new EmbeddableContact();
      return;
    }
    primaryContact.value.error = e;
    console.error('Error loading primary contact for shipping point', e);
    toast.addError({
      detail: t('errors.loadingParam', { param: t('contacts.contacts') }),
      error: e,
    });
  } finally {
    primaryContact.value.loading = false;
  }
};

const setDefaultAccessorials = (shippingPointDetails: ShippingPointDetails) => {
  const defaultAccessorials = shippingPointDetails.defaultAccessorials;
  const existingAccessorials = internalValue.value.requestedAccessorials.filter(
    (existingAcc) => {
      const isDefault = defaultAccessorials.some(
        (defaultAcc) => defaultAcc.naturalId === existingAcc.naturalId,
      );
      return !isDefault;
    },
  );

  internalValue.value.requestedAccessorials = [
    ...defaultAccessorials,
    ...existingAccessorials,
  ];
};

const getTimezone = async () => {
  try {
    isTimezoneLoading.value = true;
    cancelExistingTimezoneRequest();

    const address = new Address({
      locality: internalValue.value.location.city,
      region: internalValue.value.location.state,
      postalCode: internalValue.value.location.postalCode,
      country: internalValue.value.location.country,
    });
    const enrichedAddress = await api.shippingPoint.getGeocodeData(
      address,
      timezoneCancelRequest.value,
    );
    if (enrichedAddress.timeZoneLocale) {
      internalValue.value.location.timeZone = enrichedAddress.timeZoneLocale;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching timezone', e);
  } finally {
    isTimezoneLoading.value = false;
  }
};
const getTimeZoneDebounced = debounce(getTimezone, 500);
const cancelExistingTimezoneRequest = () => {
  if (timezoneCancelRequest.value) {
    getTimeZoneDebounced.cancel();
    timezoneCancelRequest.value.cancel();
  }
  timezoneCancelRequest.value = axios.CancelToken.source();
};

watch(
  () => errorResponse?.value,
  (newVal: Optional<ErrorResponse>) => {
    if (newVal) {
      setServerErrors(newVal);
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => internalValue.value.location.locationId,
  (locationId?: number) => {
    doesUserHaveUpdateLocationPermission.value = false;

    if (locationId) {
      loadDoesUserHavePermission(locationId);
    }
  },
  { immediate: true },
);

watch(
  () => internalValue.value.location.postalCode,
  () => {
    if (timezoneCancelRequest.value) {
      cancelExistingTimezoneRequest();
    }
    getTimeZoneDebounced();
  },
);

watch(isShowingAddressBook, (_newVal: boolean) => {
  resetSearch();
});

watch(
  modelValue,
  (newVal: Stop) => {
    const Clazz = Object.getPrototypeOf(modelValue.value).constructor;
    internalValue.value = new Clazz(newVal);
    v$.value.$clearExternalResults();
    v$.value.$reset();
  },
  { immediate: true, deep: true },
);

watch(
  internalValue,
  (newVal: Stop) => {
    if (!isEqual(newVal, modelValue.value)) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);
</script>

<style>
.stop-edit.p-panel {
  --p-panel-border-radius: var(--p-border-radius-sm);
  --p-panel-header-background: white;
  --p-panel-header-border-color: transparent transparent var(--divider-color)
    var(--stop-type-accent-color);
  --p-panel-header-border-width: 0 0 1px var(--sp-1);
  --p-panel-header-border-radius: var(--p-border-radius-sm)
    var(--p-border-radius-sm) 0 0;
  --p-panel-header-color: var(--p-gray-600);
  --p-panel-header-padding: 0 var(--sp-6) 0 0;
  --p-panel-content-padding: var(--sp-3) 0;

  min-width: 600px;

  &.warning {
    --p-panel-header-color: var(--warning-color);
    --stop-type-accent-color: var(--warning-color);

    .shipment-stop-type {
      --p-select-placeholder-color: var(--warning-color);
    }
  }

  &.error {
    --p-panel-header-color: var(--danger-color);
    --stop-type-accent-color: var(--danger-color);

    .shipment-stop-type {
      --p-select-placeholder-color: var(--danger-color);
    }
  }

  &.error {
    --panel-header-color: var(--danger-color);
    --stop-type-accent-color: var(--danger-color);

    .shipment-stop-type {
      --select-placeholder-color: var(--danger-color);
    }
  }

  .p-panel-header {
    font-size: 16px;
    font-weight: 500;

    .shipment-stop-header {
      display: flex;
      height: calc(var(--input-height) + var(--sp-3) + var(--sp-3));

      .shipment-stop-header-dropdown-wrapper {
        display: flex;
        flex-direction: row;
        gap: var(--gap-1);
        align-items: center;
        border-right: var(--divider);
        padding: var(--sp-3) var(--sp-3) var(--sp-3) var(--sp-6);
        width: 200px;
        white-space: nowrap;

        /*
        Same as visually-hidden utility to hide the label but keep it
        attached to the input in the a11y tree
        */
        label {
          position: absolute;
          width: 1px;
          height: 1px;
          margin: -1px;
          border: 0;
          padding: 0;
          white-space: nowrap;
          clip-path: inset(100%);
          clip: rect(0 0 0 0);
          overflow: hidden;
        }
      }

      .shipment-stop-type {
        --p-select-filled-background: var(--p-panel-background);
        --p-select-color: var(--p-panel-header-color);
        --p-select-dropdown-color: var(--p-panel-header-color);
        --p-select-shadow: none;

        border: none;
      }

      .shipment-stop-header-address {
        display: flex;
        gap: var(--gap-1);
        justify-content: space-between;
        align-items: center;
        padding: var(--sp-3);
        width: 100%;

        .location-link,
        .location-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: var(--panel-header-text-max-width);
          min-width: 0;
        }

        .location-link {
          font-size: 14px;

          &:not(:hover) {
            text-decoration: none;
          }
        }
      }
    }

    .p-panel-header-actions {
      display: flex;
      gap: var(--gap-1);
      align-items: center;
    }
  }

  .p-panel-content-container {
    .p-panel-content-wrapper {
      min-width: 0;
    }
  }

  .p-panel-footer {
    display: flex;
    justify-content: end;
    gap: var(--gap-1);
  }
}
</style>
